/**
 * Cookie Preferences Modal
 * Allows users to customize their cookie preferences
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter 
} from '@/components/ui/dialog';
import { 
  Cookie, 
  BarChart3, 
  Megaphone, 
  Wrench, 
  Lock,
  ExternalLink
} from 'lucide-react';
import { useCookieConsentContext } from '@/contexts/CookieConsentContext';
import { CookiePreferences } from '@/hooks/useCookieConsent';
import { useStaticTranslation } from '@/lib/staticTranslations';

interface CookieCategory {
  id: keyof CookiePreferences;
  titleKey: string;
  titleFallback: string;
  descKey: string;
  descFallback: string;
  icon: React.ReactNode;
  locked?: boolean;
}

const cookieCategories: CookieCategory[] = [
  {
    id: 'necessary',
    titleKey: 'necessaryTitle',
    titleFallback: 'עוגיות הכרחיות',
    descKey: 'necessaryDesc',
    descFallback: 'עוגיות אלו חיוניות לתפקוד הבסיסי של האתר, כולל אבטחה, ניהול סשן והתחברות. לא ניתן לבטל אותן.',
    icon: <Lock className="h-5 w-5" />,
    locked: true,
  },
  {
    id: 'analytics',
    titleKey: 'analyticsTitle',
    titleFallback: 'עוגיות סטטיסטיקה ואנליטיקה',
    descKey: 'analyticsDesc',
    descFallback: 'עוגיות אלו עוזרות לנו להבין כיצד מבקרים משתמשים באתר על ידי איסוף מידע אנונימי על דפוסי גלישה.',
    icon: <BarChart3 className="h-5 w-5" />,
  },
  {
    id: 'marketing',
    titleKey: 'marketingTitle',
    titleFallback: 'עוגיות שיווק ופרסום',
    descKey: 'marketingDesc',
    descFallback: 'עוגיות אלו משמשות להצגת פרסומות רלוונטיות ולמעקב אחר יעילות קמפיינים שיווקיים.',
    icon: <Megaphone className="h-5 w-5" />,
  },
  {
    id: 'functional',
    titleKey: 'functionalTitle',
    titleFallback: 'עוגיות פונקציונליות',
    descKey: 'functionalDesc',
    descFallback: 'עוגיות אלו מאפשרות פונקציות מתקדמות כמו שמירת העדפות, צפייה בסרטונים ושילוב עם רשתות חברתיות.',
    icon: <Wrench className="h-5 w-5" />,
  },
];

export const CookiePreferencesModal: React.FC = () => {
  const { 
    showPreferences, 
    closePreferences, 
    savePreferences, 
    state,
    acceptAll,
    rejectNonEssential 
  } = useCookieConsentContext();

  const { t, isHebrew } = useStaticTranslation("cookiePreferences");

  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
    functional: false,
  });

  // Initialize preferences from state when modal opens
  useEffect(() => {
    if (showPreferences) {
      setPreferences(state.preferences);
    }
  }, [showPreferences, state.preferences]);

  const handleToggle = (category: keyof CookiePreferences) => {
    if (category === 'necessary') return;
    
    setPreferences(prev => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const handleSave = () => {
    savePreferences(preferences);
  };

  return (
    <Dialog open={showPreferences} onOpenChange={(open) => !open && closePreferences()}>
      <DialogContent 
        className="max-w-2xl max-h-[90vh] overflow-y-auto z-[60]"
        dir={isHebrew ? "rtl" : "ltr"}
        aria-labelledby="cookie-preferences-title"
      >
        <DialogHeader className={isHebrew ? "text-right" : "text-left"}>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-primary/10 rounded-full">
              <Cookie className="h-5 w-5 text-primary" />
            </div>
            <DialogTitle id="cookie-preferences-title" className="text-xl">
              {t("title", "ניהול העדפות עוגיות")}
            </DialogTitle>
          </div>
          <DialogDescription className={isHebrew ? "text-right" : "text-left"}>
            {t("description", "בחר אילו סוגי עוגיות ברצונך לאפשר. העדפותיך יישמרו למשך 12 חודשים.")}
            <Link 
              to="/cookie-policy" 
              className={`text-primary hover:underline inline-flex items-center gap-1 ${isHebrew ? "mr-1" : "ml-1"}`}
            >
              {t("title", "מדיניות עוגיות מלאה")}
              <ExternalLink className="h-3 w-3" />
            </Link>
          </DialogDescription>
        </DialogHeader>

        {/* Cookie Categories */}
        <div className="space-y-4 py-4">
          {cookieCategories.map((category) => (
            <div 
              key={category.id}
              className="flex items-start gap-4 p-4 border border-border bg-muted/30 hover:bg-muted/50 transition-colors"
            >
              <div className="p-2 bg-background rounded-full text-primary shrink-0">
                {category.icon}
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-4 mb-2">
                  <h3 className="font-semibold text-foreground">
                    {t(category.titleKey, category.titleFallback)}
                  </h3>
                  <Switch
                    checked={preferences[category.id]}
                    onCheckedChange={() => handleToggle(category.id)}
                    disabled={category.locked}
                    aria-label={`${category.locked ? t("alwaysActive", "פעיל תמיד") : ''}: ${t(category.titleKey, category.titleFallback)}`}
                  />
                </div>
                <p className="text-sm text-muted-foreground">
                  {t(category.descKey, category.descFallback)}
                </p>
                {category.locked && (
                  <span className="inline-block mt-2 text-xs text-primary bg-primary/10 px-2 py-1 rounded">
                    {t("alwaysActive", "פעיל תמיד")}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2 pt-4 border-t">
          <div className="flex gap-2 flex-1">
            <Button
              variant="outline"
              onClick={rejectNonEssential}
              className="flex-1"
            >
              {t("rejectAll", "דחה הכל")}
            </Button>
            <Button
              variant="outline"
              onClick={acceptAll}
              className="flex-1"
            >
              {t("acceptAll", "אשר הכל")}
            </Button>
          </div>
          <Button 
            onClick={handleSave}
            className="bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            {t("savePreferences", "שמור העדפות")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
